import React, { useContext, useState, useEffect, useCallback } from 'react';
import { I18n } from 'aws-amplify';

import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { Header, Footer, SidebarOffcanvas, NavItems } from './../../shared/components/Panels';

import TotalUsersCard from './components/TotalUsersCard';
import NewUsersCard from './components/NewUsersCard';
import TotalLicencesCard from './components/TotalLicencesCard';
import NewLicencesCard from './components/NewLicencesCard';
import ThisPeriodNewUsersCard from './components/ThisPeriodNewUsersCard';
import ThisPeriodNewLicencesCard from './components/ThisPeriodNewLicencesCard';
import ThisPeriodMaintenanceLicencesCard from './components/ThisPeriodMaintenanceLicencesCard';
import MaintenanceChangesCard from './components/MaintenanceChangesCard';
import ExpiringLicencesCard from './components/ExpiringLicencesCard';
import ExpiredLicencesCard from './components/ExpiredLicencesCard';

import UserContext from './../../app/UserContext';

// LicenceList と同じ API 群を利用
import { fetchMyUsers } from './api/MyUsers';
import { getMyLicences } from './api/MyLicences';
// LicenceMaintenanceHistory 用ヘルパー
import { fetchMaintenanceHistories } from './api/LicenceMaintenanceHistory';

import { LICENCE } from './../../shared/constants/index';

/* ========== ヘルパー（年・月から期間を決定） ========== */
const resolvePeriod = (year, month) => {
  // month: 1〜12 想定
  const y = Number(year);
  const m = Number(month);

  const start = new Date(y, m - 1, 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(y, m, 1);
  end.setHours(0, 0, 0, 0);

  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
};

/* ========== Dashboard 本体 ========== */

const Dashboard = () => {
  const { user, isLoaded } = useContext(UserContext);
  const [showSidebar, setShowSidebar] = useState(false);

  // 対象年・月
  const today = new Date();
  const [targetYear, setTargetYear] = useState(today.getFullYear());
  const [targetMonth, setTargetMonth] = useState(today.getMonth() + 1); // 1〜12

  // テーブル表示件数
  const [tableLimit, setTableLimit] = useState(10);

  // --- ユーザ一覧（有効ユーザのみ保持：今は userMap 用） ---
  const [users, setUsers] = useState([]);

  // --- ユーザ関連 state ---
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [newUsers, setNewUsers] = useState([]);
  const [newUserCount, setNewUserCount] = useState(0);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);

  // --- ライセンス関連 state ---
  const [totalLicenceCount, setTotalLicenceCount] = useState(0);
  const [newLicences, setNewLicences] = useState([]);
  const [newLicenceCount, setNewLicenceCount] = useState(0);
  const [licenceLoading, setLicenceLoading] = useState(false);
  const [licenceError, setLicenceError] = useState(null);

  // --- 保守期限変更関連 state ---
  const [maintenanceUserCount, setMaintenanceUserCount] = useState(0);
  const [maintenanceLicenceCount, setMaintenanceLicenceCount] = useState(0);
  const [maintenanceChanges, setMaintenanceChanges] = useState([]);

  // --- 期限間近ライセンス（30日以内） ---
  const [expiringLicences, setExpiringLicences] = useState([]);

  // --- 保守期限超過ライセンス ---
  const [expiredLicences, setExpiredLicences] = useState([]);

  /** ダッシュボードデータ再取得用（年月変更＆リロードボタンから利用） */
  const loadDashboardData = useCallback(async () => {
    if (!isLoaded || !user) return;

    setUserLoading(true);
    setLicenceLoading(true);
    setUserError(null);
    setLicenceError(null);

    try {
      const { from, to } = resolvePeriod(targetYear, targetMonth);

      // === 1) 自分が owner の User 一覧を「全ページ」取得 ===
      const us = await fetchMyUsers(true, 1000);

      // 有効ユーザのみ対象
      const activeUsers = us.filter((u) => u.active);
      setUsers(activeUsers);

      // 総ユーザ数（有効）
      setTotalUserCount(activeUsers.length);

      // 今期（対象年月）新規ユーザ
      const newUsersAll = activeUsers.filter(
        (u) => u.createdAt && u.createdAt >= from && u.createdAt < to
      );
      setNewUserCount(newUsersAll.length);

      const sortedNewUsers = [...newUsersAll].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return a.createdAt < b.createdAt ? 1 : -1;
      });
      setNewUsers(sortedNewUsers);

      // userId → user の Map
      const userMap = new Map(activeUsers.map((u) => [u.id, u]));
      const myUserIds = activeUsers.map((u) => u.id);

      if (myUserIds.length === 0) {
        setTotalLicenceCount(0);
        setNewLicenceCount(0);
        setNewLicences([]);
        setMaintenanceUserCount(0);
        setMaintenanceLicenceCount(0);
        setMaintenanceChanges([]);
        setExpiringLicences([]);
        setExpiredLicences([]);
      } else {
        // === 2) ライセンス一覧を取得 ===
        const PAGE_SIZE = 500;
        let allLicences = [];
        let nextToken = null;
        let first = true;

        while (first || nextToken) {
          first = false;

          const res = await getMyLicences(
            {
              myUserIds,
              [LICENCE.TABLE_COLUMN.ACTIVE]: true,
            },
            nextToken,
            PAGE_SIZE
          );

          const pageItems = res.items ?? [];
          allLicences = allLicences.concat(pageItems);
          nextToken = res.nextToken ?? null;
          if (!nextToken) break;
        }

        setTotalLicenceCount(allLicences.length);

        // 対象年月の新規ライセンス
        const newLicencesAll = allLicences.filter(
          (lic) => lic.createdAt && lic.createdAt >= from && lic.createdAt < to
        );
        setNewLicenceCount(newLicencesAll.length);

        const sortedNewLicences = [...newLicencesAll]
          .sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            return a.createdAt < b.createdAt ? 1 : -1;
          })
          .map((lic) => {
            const u = userMap.get(lic.userId);
            const username = (u && u.username) || '';

            return {
              id: lic.id,
              userId: lic.userId,
              username,
              licenceId: lic?.licenceId,
              licenceKey: lic?.licenceKey,
              createdAt: lic?.createdAt,
            };
          });

        setNewLicences(sortedNewLicences);

        // === 2-α) 期限間近 & 期限超過ライセンス ===
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const limitDate = new Date(today);
        limitDate.setDate(limitDate.getDate() + 30);

        const licencesWithDate = allLicences
          .filter((lic) => lic.maintenancePeriod)
          .map((lic) => ({
            lic,
            date: new Date(lic.maintenancePeriod),
          }))
          .filter(({ date }) => !isNaN(date.getTime()));

        // 期限間近（今日〜30日後まで）
        const expiring = licencesWithDate
          .filter(({ date }) => date >= today && date <= limitDate)
          .sort((a, b) => a.date - b.date)
          .map(({ lic, date }) => {
            const u = userMap.get(lic.userId);
            const userId = u?.id;
            const username = (u && u.username) || '';
            const licenceKey = lic?.licenceKey;

            const diffMs = date.getTime() - today.getTime();
            const remainingDays = Math.ceil(
              diffMs / (1000 * 60 * 60 * 24)
            );

            return {
              id: lic.id,
              userId,
              username,
              licenceKey,
              maintenanceDate: lic.maintenancePeriod,
              remainingDays,
            };
          });

        setExpiringLicences(expiring);

        // 保守期限超過（今日より前）
        const expired = licencesWithDate
          .filter(({ date }) => date < today)
          .sort((a, b) => b.date - a.date) // 新しい順（最近切れたものから）
          .map(({ lic, date }) => {
            const u = userMap.get(lic.userId);
            const userId = u?.id;
            const username = (u && u.username) || '';
            const licenceKey = lic?.licenceKey;

            const diffMs = today.getTime() - date.getTime();
            const overdueDays = Math.ceil(
              diffMs / (1000 * 60 * 60 * 24)
            );

            return {
              id: lic.id,
              userId,
              username,
              licenceKey,
              maintenanceDate: lic.maintenancePeriod,
              overdueDays,
            };
          });

        setExpiredLicences(expired);

        // === 3) 保守期限変更履歴 ===
        const ownerName = user.username || user.attributes?.sub || '';

        const histories = await fetchMaintenanceHistories({
          owner: ownerName,
          from,
          to,
          myUserIds,
        });

        const historyUserIdSet = new Set(
          histories.filter((h) => h.userId).map((h) => h.userId)
        );
        setMaintenanceUserCount(historyUserIdSet.size);

        const historyLicenceIdSet = new Set(
          histories.filter((h) => h.licenceId).map((h) => h.licenceId)
        );
        setMaintenanceLicenceCount(historyLicenceIdSet.size);

        const latestHistories = [...histories]
          .sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            return a.createdAt < b.createdAt ? 1 : -1;
          })
          .map((h) => {
            const userId = h.user?.id || '';
            const username = h.user?.username || '';
            const licenceId = h.licence?.id;
            const licenceKey = h.licence?.licenceKey;

            return {
              id: h.id,
              userId,
              username,
              licenceId,
              licenceKey,
              beforePeriod: h.beforePeriod || null,
              afterPeriod: h.afterPeriod || null,
              beforeSubscriptionExpiryDateAt:
                h.beforeSubscriptionExpiryDateAt || '',
              afterSubscriptionExpiryDateAt:
                h.afterSubscriptionExpiryDateAt || '',
              beforeMaintenancePeriod: h.beforeMaintenancePeriod || '',
              afterMaintenancePeriod: h.afterMaintenancePeriod || '',
              changedAt: h.createdAt,
            };
          });

        setMaintenanceChanges(latestHistories);
      }
    } catch (e) {
      console.error(e);
      setUserError('ユーザ情報の取得に失敗しました。');
      setLicenceError('ライセンス情報の取得に失敗しました。');
    } finally {
      setUserLoading(false);
      setLicenceLoading(false);
    }
  }, [isLoaded, user, targetYear, targetMonth]);

  // 初回＋対象年月変更時の自動ロード
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (!isLoaded || !user) return null;

  // 年入力ハンドラ
  const handleYearChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!Number.isNaN(val)) {
      setTargetYear(val);
    }
  };

  // 月入力ハンドラ
  const handleMonthChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!Number.isNaN(val) && val >= 1 && val <= 12) {
      setTargetMonth(val);
    }
  };

  return (
    <>
      <Header onOpenSidebar={() => setShowSidebar(true)} />
      <SidebarOffcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
      />

      <Container fluid className="p-0">
        <Row className="g-0">
          {/* 左：サイドバー */}
          <Col lg={2} className="p-0 d-none d-lg-block sidebar-col">
            <NavItems />
          </Col>

          {/* 右：メイン */}
          <Col lg={10} className="px-3 main-content">
            <Container fluid className="py-3">
              <Row>
                <Col xs={12} className="mb-4 mt-3">
                  <h1>{I18n.get('title_dashboard')}</h1>
                </Col>
              </Row>
            </Container>

            <Container fluid className="py-3">
              {/* 対象年月＋件数＋リロードボタン */}
              <Row className="mb-3">
                <Col xs={12} md={5}>
                  <Form>
                    <Form.Label className="me-2">対象年月</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      style={{ maxWidth: 100, display: 'inline-block' }}
                      value={targetYear}
                      onChange={handleYearChange}
                    />
                    <span className="mx-1">年</span>
                    <Form.Select
                      size="sm"
                      style={{ maxWidth: 90, display: 'inline-block' }}
                      value={targetMonth}
                      onChange={handleMonthChange}
                    >
                      {[...Array(12)].map((_, idx) => {
                        const m = idx + 1;
                        return (
                          <option key={m} value={m}>
                            {m}月
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form>
                </Col>

                <Col xs={12} md={3} className="mt-2 mt-md-0">
                  <Form.Label className="me-2">表示件数</Form.Label>
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 120, display: 'inline-block' }}
                    value={tableLimit}
                    onChange={(e) => setTableLimit(Number(e.target.value))}
                  >
                    <option value={5}>5件</option>
                    <option value={10}>10件</option>
                    <option value={20}>20件</option>
                    <option value={50}>50件</option>
                    <option value={100}>100件</option>
                  </Form.Select>
                </Col>

                <Col xs={12} md={4} className="mt-2 mt-md-0 text-md-end">
                  <Button
                    style={{ cursor: 'pointer' }}
                    size="sm"
                    variant="outline-secondary"
                    className="ms-2"
                    onClick={loadDashboardData}
                    disabled={userLoading || licenceLoading}
                    aria-label="再読み込み"
                  >
                    <ArrowClockwise />
                  </Button>
                </Col>
              </Row>

              {(userError || licenceError) && (
                <Row className="mb-3">
                  <Col>
                    <div className="text-danger small">
                      {userError}
                      {licenceError && (
                        <>
                          <br />
                          {licenceError}
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              )}

              {/* KPIカード：上段 */}
              <Row className="mb-3">
                <Col md={3} sm={6}>
                  <TotalUsersCard
                    loading={userLoading}
                    count={totalUserCount}
                  />
                </Col>

                <Col md={3} sm={6}>
                  <TotalLicencesCard
                    loading={licenceLoading}
                    count={totalLicenceCount}
                  />
                </Col>
              </Row>

              {/* KPIカード：下段 */}
              <Row className="mb-3">
                <Col md={4} sm={6}>
                  <ThisPeriodNewUsersCard
                    loading={userLoading}
                    count={newUserCount}
                  />
                </Col>

                <Col md={4} sm={6} className="mt-3 mt-md-0">
                  <ThisPeriodNewLicencesCard
                    loading={licenceLoading}
                    count={newLicenceCount}
                  />
                </Col>

                <Col md={4} sm={6} className="mt-3 mt-md-0">
                  <ThisPeriodMaintenanceLicencesCard
                    loading={licenceLoading}
                    count={maintenanceLicenceCount}
                  />
                </Col>
              </Row>

              {/* 今期新規ユーザ / 今期新規ライセンス */}
              <Row className="mb-3">
                <Col md={6}>
                  <NewUsersCard
                    loading={userLoading}
                    users={newUsers}
                    totalCount={newUserCount}
                    limit={tableLimit}
                  />
                </Col>

                <Col md={6} className="mt-3 mt-md-0">
                  <NewLicencesCard
                    loading={licenceLoading}
                    licences={newLicences}
                    totalCount={newLicenceCount}
                    limit={tableLimit}
                  />
                </Col>
              </Row>

              {/* 有効／保守期限変更ライセンス */}
              <Row className="mb-3">
                <Col md={12}>
                  <MaintenanceChangesCard
                    loading={licenceLoading}
                    totalCount={maintenanceLicenceCount}
                    changes={maintenanceChanges}
                    limit={tableLimit}
                  />
                </Col>
              </Row>

              <hr />

              {/* 期限間近ライセンス */}
              <Row className="mb-3">
                <Col md={12}>
                  <ExpiringLicencesCard
                    loading={licenceLoading}
                    licences={expiringLicences}
                    limit={tableLimit}
                  />
                </Col>
              </Row>

              {/* 保守期限超過ライセンス */}
              <Row>
                <Col md={12}>
                  <ExpiredLicencesCard
                    loading={licenceLoading}
                    licences={expiredLicences}
                    limit={tableLimit}
                  />
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default Dashboard;
